<template>
    <div class="table-complete-container" @scroll="scroll">

        <!--
            Float Header
            Fixed to top of container so that headers are always visible when scrolling vertically
        -->
        <table-item
                ref="stickyHeader"
                class="sticky-header"
                :class="{ 'is-visible': showFloatHeader }"
                :columns="columns"
                :rows="displayRows"
                :filter_data="filterData"
                :visible_filter_data="visibleFilterData"
                :active_sort="active_sort"
                :active_filters="activeFilters"
                :show_body="false"
                :sortable="sortable"
                :anchorable="anchorable"
                :display_anchors="display_anchors"
                :draggable="draggable"
                :resize_columns="resize_columns"
                :comparison_filters="comparisonFilters"
                @sort="sort"
                @comparison_filter="comparison_filter"
                @add_filter="add_filter"
                @remove_filter="remove_filter"
                @clear_filters="clear_filters"
                @toggle_sticky="toggleSticky"
                @start_resize="startResize"
                @grab_column="grabColumn"
                @check_drop_column="checkDropColumn"
                @leave_drop_column="leaveDropColumn"
                @release_column="releaseColumn"
                @drop_column="dropColumn"
                @row_clicked="rowClicked"
                v-if="sticky_header"
        ></table-item>

        <!--
        Floating Left Aligned Columns
        "Anchored" columns are displayed on the left of the container so that the anchored row contents are visible.
        Headers are visible when scrolled horizontally (but not vertically; see below)
        -->
        <table-item
               ref="stickyColumns"
               class="sticky-columns"
               :class="{ 'is-visible': showFloatColumns }"
               :columns="columns"
               :rows="displayRows"
               :filter_data="filterData"
               :visible_filter_data="visibleFilterData"
               :active_sort="active_sort"
               :active_filters="activeFilters"
               :sticky_only="true"
               :sortable="sortable"
               :anchorable="anchorable"
               :display_anchors="display_anchors"
               :draggable="draggable"
               :resize_columns="resize_columns"
               :comparison_filters="comparisonFilters"
               @sort="sort"
               @comparison_filter="comparison_filter"
               @add_filter="add_filter"
               @remove_filter="remove_filter"
               @clear_filters="clear_filters"
               @toggle_sticky="toggleSticky"
               @start_resize="startResize"
               @grab_column="grabColumn"
               @check_drop_column="checkDropColumn"
               @leave_drop_column="leaveDropColumn"
               @release_column="releaseColumn"
               @drop_column="dropColumn"
               @toggle_hover="toggleHover"
               @row_clicked="rowClicked"
               v-if="anchorable"
        ></table-item>

        <!--
        Floating Header for Anchored Columns
        Displays headers for anchored columns when table is scrolled both horizontally and vertically
        -->
        <table-item
               ref="stickyColumnsHeaders"
               class="sticky-columns-headers"
               :class="{ 'is-visible': showFloatColumnsHeader }"
               :columns="columns"
               :rows="displayRows"
               :filter_data="filterData"
               :visible_filter_data="visibleFilterData"
               :active_sort="active_sort"
               :active_filters="activeFilters"
               :show_body="false"
               :sticky_only="true"
               :sortable="sortable"
               :anchorable="anchorable"
               :display_anchors="display_anchors"
               :draggable="draggable"
               :resize_columns="resize_columns"
               :comparison_filters="comparisonFilters"
               @sort="sort"
               @comparison_filter="comparison_filter"
               @add_filter="add_filter"
               @remove_filter="remove_filter"
               @clear_filters="clear_filters"
               @toggle_sticky="toggleSticky"
               @start_resize="startResize"
               @grab_column="grabColumn"
               @check_drop_column="checkDropColumn"
               @leave_drop_column="leaveDropColumn"
               @release_column="releaseColumn"
               @drop_column="dropColumn"
               @row_clicked="rowClicked"
               v-if="sticky_header && anchorable"
        ></table-item>


        <!--
        Data Table
        This is the primary table.  All header sizes and positions are relative to this table.
        -->
        <table-item
               ref="dataTable"
               class="data-table"
               :rows="displayRows"
               :filter_data="filterData"
               :visible_filter_data="visibleFilterData"
               :active_sort="active_sort"
               :active_filters="activeFilters"
               :columns="columns"
               :sortable="sortable"
               :anchorable="anchorable"
               :display_anchors="display_anchors"
               :draggable="draggable"
               :resize_columns="resize_columns"
               :comparison_filters="comparisonFilters"
               @sort="sort"
               @comparison_filter="comparison_filter"
               @add_filter="add_filter"
               @remove_filter="remove_filter"
               @clear_filters="clear_filters"
               @toggle_sticky="toggleSticky"
               @start_resize="startResize"
               @grab_column="grabColumn"
               @check_drop_column="checkDropColumn"
               @leave_drop_column="leaveDropColumn"
               @release_column="releaseColumn"
               @drop_column="dropColumn"
               @toggle_hover="toggleHover"
               @row_clicked="rowClicked"
        ></table-item>


        <div class="resizing-overlay"
             v-if="isResizingColumn"
             @mousemove="resize"
             @mouseup="endResize"
        ></div>
    </div>
</template>

<style lang="scss">

    @import '../../sass/_global_variables.scss';

    $drag-color: #E5C643;

    .table-complete-container {
        position: relative;
        max-height: 100%;
        max-width: 100%;
        overflow: auto;
    }

    .table-item > table {
        th, td {
            &.is-dragging,
            &.is-dragging .column-anchor,
            &.is-dragging .column-sort {
                transition: background-color 200ms ease,
                color 200ms ease;
                background-color: $drag-color;
                color: white;
            }

            &.can-drop {
                .column-separator {
                    display: none;
                }
                border-left-color: $med-blue !important;
            }
        }

        th {
            &.can-drop {
                > .cell-content {
                    border-left-color: transparent !important;
                }
            }
        }
    }
    tr.border_bottom td {
        border-bottom:1pt solid #c1c1c1 !important;
    }


    .sticky-header,
    .sticky-columns,
    .sticky-columns-headers {
        position: absolute;
        left: 0px;
        top: 0px;
        visibility: hidden;

        &.is-visible {
            visibility: visible;
        }
    }

    .sticky-header {
        > table {
            box-shadow: 0px 2px 10px $shadow-gray;
        }
        z-index: 9;
    }

    .sticky-columns {
        z-index: 8;
    }

    .sticky-columns-headers {
        z-index: 10;
    }

    .sticky-columns,
    .sticky-columns-headers {
        > table {
            box-shadow: 2px 0px 10px $shadow-gray;
            border-right: 1px solid $border-gray;
        }
    }

    .resizing-overlay {
        position: fixed;
        left: 0px;
        top: 0px;
        width: 100%;
        height: 100%;
        cursor: ew-resize;
        background-color: transparent;
        z-index: 100000;
    }

</style>

<script>
    export default{
        data: function() {
            return {
                containerEl: $(),
                tableEl: $(),
                floatHeaderEl: $(),
                floatColumnsEl: $(),
                floatColumnsHeaderEl: $(),
                showFloatHeader: false,
                showFloatColumns: false,
                showFloatColumnsHeader: false,
                resizingColumnIndex: undefined,
                grabbedColumn: undefined,
                allRows: [],
                active_sort: null,
                activeFilters: {},
                comparisonFilters: {}
            }
        },

        props: {
            rows : { default () { return [] }},
            columns : { default () { return [] }},
            page: { default: 1 },
            rows_per_page: { default: 0 },     // Set to 0 to display all rows
            sticky_header: { default: true },
            sortable: { default: false },
            anchorable: { default: false },
            display_anchors: { default: true },
            draggable: { default: false },
            resize_columns: { default: false }
        },

        computed: {
            filterData () {
                return this.rows.map(row => {
                    let filter_data = {}
                    this.columns.forEach(column => {
                        filter_data[column.id] = _.has(row,[column.id,'value']) ? row[column.id].value : null
                    })
                    return { data: filter_data }
                })
            },

            visibleFilterData () {
                return this.rows.filter(row => !row.hidden && (!row.failed_comps || row.failed_comps.length == 0))
                    .map(row => {
                        let filter_data = {}
                        this.columns.forEach(column => {
                            filter_data[column.id] = _.has(row,[column.id,'value']) ? row[column.id].value : null
                        })
                        return { data: filter_data }
                    })
            },

            rowStartIndex: function() {
                if (this.rows_per_page <= 0) return 0;

                return (this.page * this.rows_per_page - this.rows_per_page);
            },

            rowEndIndex: function() {
                if (this.rows_per_page <= 0) return this.rows.length;

                var endIndex = this.page * this.rows_per_page;
                if (endIndex > this.rows.length - 1) endIndex = this.rows.length;

                return endIndex;
            },

            displayRows: function() {
                return this.allRows.filter(item => !item.hidden && (!item.failed_comps || item.failed_comps.length == 0))
                    .filter((item,index,array) => index >= this.rowStartIndex && index < this.rowEndIndex)
            },

            isDraggingColumn: function() {
                if (this.grabbedColumn) return true;
                else return false;
            },

            isResizingColumn: function() {
                if (this.resizingColumnIndex >= 0) return true;
                else return false;
            }
        },

        watch: {
            rows () {
                this.allRows = this.rows.slice()
            },

            displayRows: function() {
                Vue.nextTick(this.resizeFloatTables);
            }
        },

        methods: {
            /****************************************************
            Initialization
            *****************************************************/
            initializeElementReferences: function() {
                this.containerEl = $(this.$el);
                this.floatHeaderEl = this.sticky_header ? $(this.$refs.stickyHeader.$refs.table) : $();
                this.tableEl = $(this.$refs.dataTable.$refs.table);
                this.floatColumnsEl = this.anchorable ? $(this.$refs.stickyColumns.$refs.table) : $();
                this.floatColumnsHeaderEl = this.anchorable ? $(this.$refs.stickyColumnsHeaders.$refs.table) : $();
            },

            initializeDefaults: function() {
                var sortColumn;
                for (var i=0;i<this.columns.length;i++) {
                    var column = this.columns[i];
                    if (typeof column.stick !== 'boolean') {
                        this.$set(column, 'stick', false);
                    }
                    if (typeof column.sort !== 'string') {
                        this.$set(column, 'sort', '');
                    }
                    if (column.sort) {
                        if (sortColumn) column.sort = '';
                        else sortColumn = column;
                    }
                }
            },


            /****************************************************
            Row Hovering
            *****************************************************/
            toggleHover: function(rowIndex, isHovering) {
                var hoverRows = $(this.$refs.dataTable.$refs.rows[rowIndex]);

                if (this.anchorable) {
                    hoverRows = hoverRows.add(this.$refs.stickyColumns.$refs.rows[rowIndex]);
                }

                if (isHovering) hoverRows.addClass('is-hovering');
                else hoverRows.removeClass('is-hovering');
            },


            /****************************************************
            Manage Floating Tables
            *****************************************************/
            scroll: function() {
                this.positionFloatTables();

                var shouldResizeHeaders = false;

                if (this.sticky_header) {
                    this.showFloatHeader = true;
                    shouldResizeHeaders = true;
                }

                if (this.anchorable) {
                    if (this.tableEl.offset().left == this.floatColumnsEl.offset().left) {
                        this.showFloatColumns = false;
                    }
                    else if (!this.showFloatColumns) {
                        this.showFloatColumns = true;
                        shouldResizeHeaders = true
                    }

                    if (this.tableEl.offset().left == this.floatColumnsHeaderEl.offset().left || this.tableEl.offset().top == this.floatColumnsHeaderEl.offset().top) {
                        this.showFloatColumnsHeader = false;
                    }
                    else if (!this.showFloatColumnsHeader) {
                        this.showFloatColumnsHeader = true;
                        shouldResizeHeaders = true
                    }
                }

                if (shouldResizeHeaders) this.resizeFloatTables();
            },

            positionFloatTables: function() {
                var containerOffset = this.containerEl.offset();
                var tableOffset = this.tableEl.offset();
                this.floatHeaderEl.offset({ top: containerOffset.top, left: tableOffset.left });
                this.floatColumnsEl.offset({ top: tableOffset.top, left: containerOffset.left });
                this.floatColumnsHeaderEl.offset({ top: containerOffset.top, left: containerOffset.left });
            },

            resizeFloatTables: function() {
                var headerSelector = '> thead > tr:first-child > th';
                var refHeaders = this.tableEl.find(headerSelector);
                var floatHeaders = this.floatHeaderEl.find(headerSelector);
                var floatColumnHeaders = this.floatColumnsEl.find(headerSelector);
                var floatCOlumnHeadersHeaders = this.floatColumnsHeaderEl.find(headerSelector);
                var sizingDivSelector = '.cell-content';
                for (var i=0;i<refHeaders.length;i++) {
                    var newHeaderStyle = { width: refHeaders.eq(i).width() + 'px' };
                    floatHeaders.eq(i).children(sizingDivSelector).css(newHeaderStyle);
                    floatColumnHeaders.eq(i).children(sizingDivSelector).css(newHeaderStyle);
                    floatCOlumnHeadersHeaders.eq(i).children(sizingDivSelector).css(newHeaderStyle);
                }
                this.floatColumnsEl.children('thead').css({ height: this.tableEl.children('thead').height() + 'px' });
                this.floatColumnsHeaderEl.children('thead').css({ height: this.tableEl.children('thead').height() + 'px' });
            },


            /****************************************************
            Drag and Drop Columns
            *****************************************************/
            itemsAffectedByDragAndDrop: function(columnIndex) {
                var items = $(this.$refs.dataTable.$refs.headers[columnIndex]);

                if (this.sticky_header) {
                    items = items.add(this.$refs.stickyHeader.$refs.headers[columnIndex]);
                }
                if (this.anchorable) {
                    items = items.add(this.$refs.stickyColumns.$refs.headers[columnIndex]);
                    items = items.add(this.$refs.stickyColumnsHeaders.$refs.headers[columnIndex]);
                }

                items = items.add(this.floatColumnsEl.children('tbody').children('tr').children('td:nth-child('+(columnIndex+1)+')'));
                items = items.add(this.tableEl.children('tbody').children('tr').children('td:nth-child('+(columnIndex+1)+')'));

                return items;
            },

            grabColumn: function(columnIndex, event) {
                this.grabbedColumn = this.columns[columnIndex];

                var refHeader = $(this.$refs.dataTable.$refs.headers[columnIndex]);
                var headerClone = refHeader.clone();
                headerClone.children('.cell-content').css({ width: refHeader.children('.cell-content').outerWidth() + 'px' });

                var dragTable = $('<table class="column-drag-image"><thead><tr></tr></thead></table>');
                dragTable.find('> thead > tr:first-child').append(headerClone);
                dragTable.appendTo($('body'));

                var data = event.dataTransfer;
                data.effectAllowed = 'copy';
                data.dropEffect = 'copy';
                data.setData('text/plain',columnIndex);
                data.setDragImage(dragTable[0],17,dragTable.outerHeight() * 0.5);

                this.itemsAffectedByDragAndDrop(columnIndex).addClass('is-dragging');
            },

            checkDropColumn: function(columnIndex, event) {
                this.itemsAffectedByDragAndDrop(columnIndex).addClass('can-drop');
            },

            leaveDropColumn: function(columnIndex, event) {
                this.itemsAffectedByDragAndDrop(columnIndex).removeClass('can-drop');
            },

            releaseColumn: function(columnIndex, event) {
                this.grabbedColumn = undefined;

                $('.is-dragging').removeClass('is-dragging');
                $('.can-drop').removeClass('can-drop');
            },

            dropColumnOld: function(columnIndex, event) {
                this.releaseColumn(columnIndex, event);

                var vm = this;
                Vue.nextTick(function() {
                    var itemsToFade = vm.itemsAffectedByDragAndDrop(columnIndex);
                    itemsToFade.css('opacity',0);
                    itemsToFade.animate({ opacity: 1 }, 300, 'swing', function() {
                        itemsToFade.css('opacity','');
                    });
                    var headers = vm.$refs.dataTable.$refs.headers;
                    for (var i=0;i<headers.length;i++) {
                        var header = $(headers[i]);
                        var innerDiv = header.children('.cell-content');
                        if (innerDiv.outerWidth() < header.width()) {
                            innerDiv.css({ width: header.width() + 'px' });
                        }
                    }
                    vm.resizeFloatTables();
                });
            },

            dropColumn: function(columnIndex, event) {
                if (this.isDraggingColumn) {
                    if (this.grabbedColumn.id != this.columns[columnIndex].id) {
                        this.columns.splice(columnIndex,0,this.grabbedColumn);

                        for (var i=0;i<this.columns.length;i++) {
                            if (i != columnIndex && this.grabbedColumn.id == this.columns[i].id) {
                                this.columns.splice(i,1);
                                break;
                            }
                        }

                        var columnNewIndex;
                        for (var i=0;i<this.columns.length;i++) {
                            if (this.columns[i].id == this.grabbedColumn.id) {
                                columnNewIndex = i;
                                break;
                            }
                        }

                        var leftRelativeColumn = this.columns[columnNewIndex - 1];
                        var rightRelativeColumn = this.columns[columnNewIndex + 1];
                        if (rightRelativeColumn && rightRelativeColumn.stick) {
                            this.columns[columnNewIndex].stick = true;
                        }
                        else {
                            this.columns[columnNewIndex].stick = false;
                        }

                        var vm = this;
                        Vue.nextTick(function() {
                            var itemsToFade = vm.itemsAffectedByDragAndDrop(columnNewIndex);
                            itemsToFade.css('opacity',0);
                            itemsToFade.animate({ opacity: 1 }, 300, 'swing', function() {
                                itemsToFade.css('opacity','');
                            });
                            var headers = vm.$refs.dataTable.$refs.headers;
                            for (var i=0;i<headers.length;i++) {
                                var header = $(headers[i]);
                                var innerDiv = header.children('.cell-content');
                                if (innerDiv.outerWidth() < header.width()) {
                                    innerDiv.css({ width: header.width() + 'px' });
                                }
                            }
                            vm.resizeFloatTables();
                        });
                    }

                    this.releaseColumn();
                    this.resizeFloatTables();
                }
            },


            /****************************************************
            Resize Columns
            *****************************************************/
            startResize: function(columnIndex, event) {
                this.resizingColumnIndex = columnIndex;
            },

            resize: function(event) {
                if (this.isResizingColumn) {
                    var header = $(this.$refs.dataTable.$refs.headers[this.resizingColumnIndex]);
                    var innerDiv = header.children('.cell-content');
                    var refDiv;
                    if (this.columns[this.resizingColumnIndex].stick && this.anchorable) {
                        refDiv = $(this.$refs.stickyColumns.$refs.headers[this.resizingColumnIndex]).children('.cell-content');
                    }
                    else {
                        refDiv = innerDiv;
                    }
                    var newWidth = event.clientX - refDiv.offset().left;
                    innerDiv.css({ width: newWidth + 'px' });
                    if (innerDiv.outerWidth() < header.width()) {
                        newWidth = header.width();
                        innerDiv.css({ width: newWidth + 'px' });
                    }

                    var headersToResize = this.sticky_header ? $(this.$refs.stickyHeader.$refs.headers[this.resizingColumnIndex]) : $();

                    if (this.anchorable) {
                        headersToResize = headersToResize.add($(this.$refs.stickyColumns.$refs.headers[this.resizingColumnIndex]));
                        headersToResize = headersToResize.add($(this.$refs.stickyColumnsHeaders.$refs.headers[this.resizingColumnIndex]));
                    }

                    headersToResize.children('.cell-content').css({ width: newWidth });
                }
            },

            endResize: function(columnIndex, event) {
                this.resizingColumnIndex = undefined;
            },


            /****************************************************
            Manage Sticky Columns
            *****************************************************/
            toggleSticky: function(columnIndex) {
                var column = this.columns[columnIndex];
                column.stick = !column.stick;

                this.columns.splice(columnIndex,1);
                for (var i=0;i<this.columns.length;i++) {
                    if (!this.columns[i].stick) {
                        this.columns.splice(i,0,column);
                        break;
                    }
                }

                Vue.nextTick(this.resizeFloatTables);
            },


            /****************************************************
            Sort Columns
            *****************************************************/
            sort (sort_info) {
                this.allRows.sort(function(a,b) {
                    a = _.has(a,[sort_info.column,'value']) ? a[sort_info.column].value : null
                    b = _.has(b,[sort_info.column,'value']) ? b[sort_info.column].value : null
                    if (a === null && b === null) return 0
                    if (a === null) return 1
                    if (b === null) return -1
                    switch((sort_info.method) ? sort_info.method : 'alph') {
                        case 'numeric':
                            return a - b;
                            break;
                        case 'date':
                            a = new Date(a);
                            b = new Date(b);
                            break;
                        default:
                    }
                    return a>b ? 1 : a<b ? -1 : 0
                });

                if (sort_info.descending) {
                    this.allRows.reverse();
                }

                this.active_sort = {
                    column: sort_info.column,
                    direction: sort_info.descending ? 'desc' : 'asc'
                }
            },

            comparison_filter (filter_info) {
                Vue.set(this.comparisonFilters,filter_info.column,filter_info.comparisons);

                let checkValue = (item) => {
                    let evalString = ""
                    filter_info.comparisons.forEach((filter,index) => {
                        if (index > 0) {
                            switch (filter.logic.toLowerCase()) {
                                case 'and':
                                    evalString += " && "
                                    break;
                                case 'or':
                                    evalString += " || "
                                    break;
                            }
                        }
                        evalString += item + filter.operator + filter.value
                    })
                    return evalString ? eval(evalString) : true
                }

                this.allRows.forEach(row => {
                    let failed_comps = row.failed_comps ? row.failed_comps : []
                    failed_comps.splice(failed_comps.indexOf(filter_info.column), 1)

                    if (filter_info.comparisons.length > 0) {
                        if (!_.has(row, [filter_info.column, 'value'])) {     // row doesn't have this item
                            failed_comps.push(filter_info.column)
                        }
                        else if (row[filter_info.column].value == 'n.d.') {
                            if (!checkValue(0)) {
                                failed_comps.push(filter_info.column)
                            }
                        }
                        else if (isNaN(row[filter_info.column].value)) {
                            failed_comps.push(filter_info.column)
                        }
                        else if (!checkValue(row[filter_info.column].value)) {
                            failed_comps.push(filter_info.column)
                        }
                    }

                    Vue.set(row,'failed_comps',failed_comps)
                })
            },

            add_filter (filter_info) {
                if (!('column' in filter_info) || !('filter' in filter_info)) {
                    return false;
                }

                const filter = filter_info.filter;

                if (!_.has(this.activeFilters,filter_info.column)) {
                    Vue.set(this.activeFilters, filter_info.column, [])
                }

                this.activeFilters[filter_info.column].push(filter);

                // First filter
                if (Object.keys(this.activeFilters).length === 1 && this.activeFilters[filter_info.column].length === 1) {
                    this.allRows.forEach((row,index) => {
                        if (_.has(row,[filter_info.column,'value']) && row[filter_info.column].value && row[filter_info.column].value == filter) {
                            Vue.set(row, 'hidden', false)
                        }
                        else {
                            Vue.set(row, 'hidden', true)
                        }
                    })
                }
                // Only filtering on 1 column
                else if (Object.keys(this.activeFilters).length === 1){
                    this.allRows.filter(row => row.hidden).forEach((row,index) => {
                        if (_.has(row,[filter_info.column,'value']) && row[filter_info.column].value && row[filter_info.column].value == filter) {
                            Vue.set(row, 'hidden', false)
                        }
                    })
                }
                // Filtering on multiple columns
                else {
                    this.allRows.filter(row => !row.hidden).forEach((row,index) => {
                        if (!_.has(row,[filter_info.column,'value']) || !row[filter_info.column].value || row[filter_info.column].value != filter) {
                            Vue.set(row, 'hidden', true)
                        }
                    })

                    let rows = this.allRows.filter(row => row.hidden)
                    for (let column in this.activeFilters) {
                        rows = rows.filter(row => _.has(row,[column,'value']) && row[column].value && this.activeFilters[column].includes(row[column].value))
                    }
                    rows.forEach((row,index) => {
                        Vue.set(row, 'hidden', false)
                    })
                }
            },

            remove_filter (filter_info) {
                if (!('column' in filter_info) || !('filter' in filter_info)) {
                    return false;
                }
                const filter = filter_info.filter;

                this.allRows.filter(row => !row.hidden).forEach((row,index) => {
                    if (_.has(row,[filter_info.column,'value']) && row[filter_info.column].value && row[filter_info.column].value == filter) {
                        Vue.set(row, 'hidden', true)
                    }
                })

                this.activeFilters[filter_info.column].splice(this.activeFilters[filter_info.column].indexOf(filter_info.filter),1);

                if (this.activeFilters[filter_info.column].length === 0) {
                    delete this.activeFilters[filter_info.column]
                }

                if (Object.keys(this.activeFilters).length === 0) {
                    this.allRows.filter(row => row.hidden).forEach((row, index) => {
                        Vue.set(row, 'hidden', false)
                    })
                }
                else {
                    let rows = this.allRows.filter(row => row.hidden)
                    for (let column in this.activeFilters) {
                        rows = rows.filter(row => _.has(row,[column,'value']) && row[column].value && this.activeFilters[column].includes(row[column].value))
                    }
                    rows.forEach((row,index) => {
                        Vue.set(row, 'hidden', false)
                    })
                }
            },

            clear_filters (column) {
                if (typeof column !== undefined && _.has(this.activeFilters,column)) {
                    _.clone(this.activeFilters[column]).forEach(filter => {
                        this.remove_filter({ column, filter })
                    })
                }

                this.comparison_filter({ column, comparisons: [] })
            },

            rowClicked: function(index) {
                this.$emit('row_clicked',index);
            }
        },


        /****************************************************
        Prepare Vue Instance
        *****************************************************/
        created: function() {
            this.initializeDefaults();
        },

        mounted: function() {
            this.initializeElementReferences();

            var vm = this;
            $('#nav-content').on('nav:resize', function() {
                window.requestAnimationFrame(function() {
                    window.requestAnimationFrame(function() {
                        vm.resizeFloatTables();
                    });
                });
            });
        }
    }
</script>



// WEBPACK FOOTER //
// dynamic-table.vue?6f938d12